import { useCallback, useEffect, useRef } from "react";
import { pollCeleryTaskUntilTerminal } from "../services/celery";

/**
 * Hook for pages that enqueue a Celery job and poll until completion.
 *
 * Usage:
 *   const pollTask = useCancellableCeleryPoll();
 *   const { outcome, result } = await pollTask(taskId);
 *
 * Stops polling when the component unmounts or when `pollTask` is called again
 * (e.g. user starts a second upload before the first finishes).
 */
export function useCancellableCeleryPoll() {
  const abortRef = useRef(null);

  // Stop any in-flight poll when the user navigates away
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  return useCallback(async (taskId) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    return pollCeleryTaskUntilTerminal(taskId, { signal: abortRef.current.signal });
  }, []);
}
